import isEqual from "lodash/isEqual";
import {
    decrementCertificateCountAPICompleted,
    incrementCertificateCountAPICompleted,
    initialState,
    reloadCertificateStatuses,
    setFormState,
    setIsLoadingCertificateCount,
    setLoading,
    setNoDataType,
    setNoSearch,
    setPage,
    setPageToNull,
} from ".";
import axios from "../../axios";
import {
    customerRelationshipOptions,
    getCustomerRelationShip,
    processIdentifiers,
} from "../../features/search/Filters/utility";
import toast from "../../hooks/toast";
import { CertificateStatuses, completeRequestStatuses } from "../../shared/constants";
import { deduplicateRequest } from "../../shared/services/apiDeduplicationService";
import {
    buildApiV3Url,
    fixSearchStringForCertificates,
    lowerCaseAllExceptFirst,
    transformExemptionData,
} from "../../shared/Utils";
import { getStoredItem } from "../../shared/Utils.js";
import { setCertificateIds } from "../bulkUpdateSlice";
import { mapLocalJurisdictionsToCertificateJurisdictionCodes } from "../../features/search/Filters/mapLocalJurisdictionsToCertificateJurisdictionCodes";

const [showToast] = toast();

// Module-level promises to track in-flight requests and prevent duplicate concurrent API calls
let certificateSearchPromise = null;
let certificateCountPromise = null;

// Helper functions
const isFiltersEmpty = filtersState => {
    const filtersWithoutSavedSearch = { ...filtersState };
    delete filtersWithoutSavedSearch.savedSearch;
    delete filtersWithoutSavedSearch.isCustomerDetails;
    delete filtersWithoutSavedSearch.isCertificateDetails;
    const initialStateWithoutSavedSearch = { ...initialState.certificateFiltersState };
    delete initialStateWithoutSavedSearch.savedSearch;
    delete initialStateWithoutSavedSearch.isCustomerDetails;
    delete initialStateWithoutSavedSearch.isCertificateDetails;

    return isEqual(filtersWithoutSavedSearch, initialStateWithoutSavedSearch);
};

const getExemptionStatuses = (certificateFiltersState, statuses) => {
    const exemptionStatuses = [];
    const hasValidStatus = completeRequestStatuses.some(status =>
        certificateFiltersState?.certificateStatus?.includes(status)
    );
    if (hasValidStatus) {
        const exemptionStatus = statuses?.filter(
            status =>
                status?.name === CertificateStatuses.Complete ||
                status?.name === CertificateStatuses.PENDING_FUTURE
        );
        exemptionStatus?.forEach(ele => exemptionStatuses.push(ele?.id));
    }
    if (certificateFiltersState?.certificateStatus?.includes(CertificateStatuses.PendingFuture)) {
        exemptionStatuses.push(
            statuses.filter(status => status?.name === CertificateStatuses.PENDING_FUTURE)[0]?.id
        );
    }
    if (
        certificateFiltersState?.certificateStatus?.includes(
            lowerCaseAllExceptFirst(CertificateStatuses.Pending)
        )
    ) {
        exemptionStatuses.push(
            statuses.filter(status => status?.name === CertificateStatuses.Pending)[0]?.id
        );
    }

    return exemptionStatuses;
};

const getSearchVal = (formState, certificateFiltersState) => {
    if (formState.nameOrCodeWhileLoading !== "") return formState.nameOrCodeWhileLoading;
    if (certificateFiltersState?.nameOrCode !== "") return certificateFiltersState.nameOrCode;
    return formState.nameOrCode;
};

const createRequestBody = (
    searchVal,
    certificateFiltersState,
    pageSize,
    pageNumber,
    sortColumn,
    exemptionStatuses,
    searchType
) => ({
    pageSize,
    pageNumber,
    orderBy: sortColumn[0],
    orderByDirection: sortColumn[1] ? "ASC" : "DESC",
    nameOrCode: searchVal ?? "",
    purchaseOrderNumber: certificateFiltersState.invoiceNumber,
    isPrimary: certificateFiltersState.isCertificateActive,
    returnEmptyEmailRecords: certificateFiltersState.isMissingEmailAddress,
    isBillTo: getCustomerRelationShip(
        certificateFiltersState.customerRelationShip,
        customerRelationshipOptions.value.isBillTo
    ),
    isShipTo: getCustomerRelationShip(
        certificateFiltersState.customerRelationShip,
        customerRelationshipOptions.value.isShipTo
    ),
    isDuplicate: certificateFiltersState.isDuplicateRelation,
    isSingle: certificateFiltersState.isPurchaseOrder,
    certificateImageExists: certificateFiltersState.isCertificateImage,
    customerCreatedFrom: certificateFiltersState.customerCreatedDateAfter || null,
    customerCreatedTo: certificateFiltersState.customerCreatedDateBefore || null,
    customerModifiedFrom: certificateFiltersState.customerModifiedDateAfter || null,
    customerModifiedTo: certificateFiltersState.customerModifiedDateBefore || null,
    certificateCreatedFrom: certificateFiltersState.certificateCreatedDateAfter || null,
    certificateCreatedTo: certificateFiltersState.certificateCreatedDateBefore || null,
    certificateModifiedFrom: certificateFiltersState.certificateModifiedDateAfter || null,
    certificateModifiedTo: certificateFiltersState.certificateModifiedDateBefore || null,
    certificateEffectiveFrom: certificateFiltersState.certificateEffectiveDateAfter || null,
    certificateEffectiveTo: certificateFiltersState.certificateEffectiveDateBefore || null,
    certificateExpirationFrom: certificateFiltersState.certificateExpirationDateAfter || null,
    certificateExpirationTo: certificateFiltersState.certificateExpirationDateBefore || null,
    certificateValidatedFrom: certificateFiltersState.certificateValidatedDateAfter || null,
    certificateValidatedTo: certificateFiltersState.certificateValidatedDateBefore || null,
    status: certificateFiltersState.certificateStatus,
    exemptionStatus: exemptionStatuses,
    exposureZones: certificateFiltersState.certificateRegions,
    exemptionReasons: certificateFiltersState.exemptReasons,
    invalidReasons: certificateFiltersState.invalidReasons,
    customerRegions: certificateFiltersState.customerRegions,
    productExemptionCategories: certificateFiltersState.productExemptionCategories,
    certificateIds: processIdentifiers(certificateFiltersState.certificateIds, value =>
        Number(fixSearchStringForCertificates(value))
    ).filter(id => id !== 0 && Number.isFinite(id)),
    customerCodes: processIdentifiers(
        certificateFiltersState.customerCodes,
        fixSearchStringForCertificates
    ),
    activeCertificate: certificateFiltersState.isCertActive,
    certificateHasCustomers: certificateFiltersState.certificateHasCustomers,
    certificateCustomFields: certificateFiltersState.certificateCustomFields,
    customerCustomFields: certificateFiltersState.customerCustomFields,
    certificatesLabels: certificateFiltersState.certificatesLabels,
    excludeCertificateLabels: certificateFiltersState.excludeCertificateLabels,
    customersLabels: certificateFiltersState.customersLabels,
    reviewedBy: certificateFiltersState.reviewedBy,
    companyDivisionId: certificateFiltersState.companyGroup.companyDivisionId,
    companyIds: certificateFiltersState.companyGroup.companyIds,
    excludeCustomerLabels: certificateFiltersState.excludeCustomerLabels,
    searchType,
    customerExposures: certificateFiltersState.customerExposures,
    certificateJurisdictionCodes: mapLocalJurisdictionsToCertificateJurisdictionCodes(
        certificateFiltersState.localJurisdictions
    ),
});

// Loading State Handling
const handleLoadingState = (dispatch, formState, searchVal, skipLoadingCheck) => {
    dispatch(setLoading(true));
    if (formState.isloading && !skipLoadingCheck) {
        dispatch(
            setFormState({
                ...formState,
                nameOrCodeWhileLoading: searchVal,
            })
        );
        return true; // Skip API call
    }
    return false;
};

// API Call & Response Handling
const fetchCertificatesData = async (requestBody, dispatch, onLanding, pageSize) => {
    try {
        const apiResponse = await axios.post(buildApiV3Url(`certificates/search`), requestBody, {
            withCredentials: true,
        });

        if (!apiResponse || !apiResponse.data) {
            dispatch(setNoDataType(onLanding ? "onLanding" : "onSearch"));
            dispatch(setPageToNull(pageSize));
            dispatch(setLoading(false));
            return null;
        } else {
            dispatch(setNoDataType(""));
        }

        return apiResponse;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error fetching certificates:", error);
        dispatch(setLoading(false));
        return null;
    }
};

// Pagination Handling
const handlePagination = (
    apiResponse,
    pageData,
    dispatch,
    pageSize,
    pageNumber,
    paginateDetails,
    response
) => {
    const paginateData = paginateDetails
        ? {
              pageNumber,
              startIndex: paginateDetails.startIndex,
              endIndex: paginateDetails.endIndex,
              rowsPerPage: paginateDetails.rowsPerPage,
              totalRecords: pageData.totalRecords,
              indeterminate: false,
          }
        : {
              pageNumber,
              startIndex: 0,
              endIndex: 19,
              rowsPerPage: pageSize,
              totalRecords:
                  response?.isCertificateCountAPICompleted === 0 ? pageData.totalRecords : 20,
              indeterminate: response?.isCertificateCountAPICompleted !== 0,
          };

    dispatch(
        setPage({
            data: response.data.value,
            paginate: paginateData,
        })
    );
};

// Certificate Count API Handling with deduplication
const fetchCertificateCount = async (requestBody, dispatch, getState) => {
    certificateCountPromise = deduplicateRequest(certificateCountPromise, async () => {
        try {
            const res = await axios.post(buildApiV3Url(`certificates/search-count`), requestBody, {
                withCredentials: true,
            });

            const { page } = getState().certificate;
            const pageData = { ...page.paginate };
            pageData.totalRecords = res?.data;
            pageData.indeterminate = false;

            dispatch(
                setPage({
                    data: page.data,
                    paginate: pageData,
                })
            );
            return res;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("Error fetching certificate count:", error);
            return null;
        }
    });

    try {
        return await certificateCountPromise;
    } finally {
        certificateCountPromise = null;
    }
};

const fetchCertificatesSearchAPI =
    (
        onLanding,
        paginateDetails,
        pageSize,
        pageNumber = 1,
        skipLoadingCheck = false,
        searchType = 0
    ) =>
    async (dispatch, getState) => {
        const { formState, certificateFiltersState, sortColumn, certificateStatuses } =
            getState().certificate;
        const { rowsPerPage } = getState().session;
        if (
            certificateFiltersState.certificateIdError.length > 0 ||
            certificateFiltersState.customerCodeError.length > 0
        ) {
            showToast(
                "error",
                "Please fix the highlighted filter issues before submitting your search."
            );
            return;
        }

        pageSize = pageSize || rowsPerPage;
        const searchVal = getSearchVal(formState, certificateFiltersState);

        // Handle loading state early and return if skip
        if (handleLoadingState(dispatch, formState, searchVal, skipLoadingCheck)) return;

        await reloadCertificateStatuses(dispatch);
        const statuses = getStoredItem("certificateStatuses") || certificateStatuses;
        const exemptionStatuses = getExemptionStatuses(certificateFiltersState, statuses);

        // Handle filter state and search conditions
        if (isFiltersEmpty(certificateFiltersState) && searchVal === "") {
            dispatch(setNoSearch(true));
        } else {
            dispatch(setNoSearch(false));
        }

        const requestBody = createRequestBody(
            searchVal,
            certificateFiltersState,
            pageSize,
            pageNumber,
            sortColumn,
            exemptionStatuses,
            searchType
        );

        // Use deduplication to prevent concurrent duplicate search calls
        certificateSearchPromise = deduplicateRequest(certificateSearchPromise, async () => {
            const apiResponse = await fetchCertificatesData(
                requestBody,
                dispatch,
                onLanding,
                pageSize
            );

            if (!apiResponse) return null;

            const response = transformExemptionData(apiResponse, false, false, [
                "expectedTaxCodeName",
                "taxCodeName",
            ]);
            handlePagination(
                apiResponse,
                getState().certificate.page.paginate,
                dispatch,
                pageSize,
                pageNumber,
                paginateDetails,
                response
            );

            // Set the certificate IDs
            const certificateIds = response.data.value.map(element => element.certificateId);
            dispatch(setCertificateIds(certificateIds));

            return { requestBody, response };
        });

        try {
            const result = await certificateSearchPromise;
            if (!result) return;

            // Clear loading state and reset search value
            dispatch(setLoading(false));

            if (formState.nameOrCodeWhileLoading !== "") {
                await dispatch(
                    setFormState({
                        ...formState,
                        nameOrCodeWhileLoading: "",
                    })
                );
            }

            // Fetch certificate count if necessary
            dispatch(setIsLoadingCertificateCount(true));
            dispatch(incrementCertificateCountAPICompleted());
            await fetchCertificateCount(result.requestBody, dispatch, getState);
            dispatch(decrementCertificateCountAPICompleted());
            dispatch(setIsLoadingCertificateCount(false));
        } finally {
            certificateSearchPromise = null;
        }
    };

export default fetchCertificatesSearchAPI;
